import { ElementRef, Injectable } from '@angular/core';
import { Http2Service } from '../services/http2.service';
import { environment } from '../../environments/environment';
import { IEsriLayer } from './../models';
import { AoiComponent } from './../components/aoi/aoi.component';

@Injectable()
export class UtilsService {

    identify: any = null;
    aoi: AoiComponent;
    aoiWindow: ElementRef<any>;
    tooltip: any;
    videoLogWindow: ElementRef<any>;

    constructor(
        private readonly http2Service: Http2Service
    ) { }

    get mcAuth(): any {
        return document.getElementById('mc-auth');
    }

    get mcMap(): any {
        return document.getElementById('mc-map');
    }

    get esriMap(): any {
        var mcMap = document.getElementById('mc-map');
        return (<any>mcMap).map;
    }

    get esriMapView(): any {
        var mcMap = document.getElementById('mc-map');
        return (<any>mcMap).mapView;
    }

    get mapService(): any {
        return this.mcMap.MapService;
    }

    get mapLayers(): any[] {
        const mapLayers: any[] = [];
        for (const layer of this.mcMap.map.layers.toArray() as any[]) {
            if (layer.type === 'group' && Symbol.iterator in Object(layer.layers)) {
                for (const sub of layer.layers) {
                    mapLayers.push(sub);
                }
            } else {
                mapLayers.push(layer);
            }
        }
        return mapLayers;
    }

    openAoi() {
        this.aoi.activateTab();
        this.aoiWindow.nativeElement.open();
    }

    getLayerByUniqueId(layerId: any): IEsriLayer {
        return this.mapLayers.filter(l => l.id === layerId)[0];
    }

    getRootUrl() {
        const appRootUrl = '/' + environment.pdauthAppName.toLowerCase();
        if (window.location.href.indexOf(appRootUrl) >= 0) {
            return appRootUrl;
        }
        return '';
    }

    getCleanValue(value, translation) {
        if (value && value.split !== undefined) {
            let values = value.split('-');
            value = values[0].trim();
        }
        return value;
    }

    getParameterByName(key, url: string = undefined) {
        if (!url) {
            url = window.location.href
        }
        key = key.replace(/[\[\]]/g, '\\$&');
        var regex = new RegExp('[?&]' + key + '(=([^&#]*)|&|#|$)'),
            results = regex.exec(url);
        if (!results) return null;
        if (!results[2]) return '';
        return decodeURIComponent(results[2].replace(/\+/g, ' '));
    }

    addMessage(message: string) {
        let applicationMessage = new CustomEvent('applicationMessage', { detail: { message: message, level: 2, isPrivate: false }, composed: true });
        window.dispatchEvent(applicationMessage);
    }

    httpGet(urlComponents) {
        var url = this.getRestUrlFromUrlComponents(urlComponents);
        return this.http2Service.get<any>(url, { withCredentials: true });
    }

    httpDelete(urlComponents) {
        var url = this.getRestUrlFromUrlComponents(urlComponents);
        return this.http2Service.delete(url, { withCredentials: true });
    }

    httpPost(urlComponents: Array<string>, data: any) {
        var url = this.getRestUrlFromUrlComponents(urlComponents);
        return this.http2Service.post<any>(url, data, { withCredentials: true });
    }

    hex2a(hex) {
        var str = '';
        for (var i = 0; i < hex.length; i += 2)
            str += String.fromCharCode(parseInt(hex.substr(i, 2), 16));
        return str;
    }

    postMessage(name: string, dataName?: string, data?: any): void {
        let message: any = { 'name': name };
        if (dataName) {
            message[dataName] = data;
        }
        top.postMessage(message, '*');
    }

    async graphqlRequest<T = any>(query: string, variables: any, method: string = 'GET', dataPath: string = null) {
        let queryName: string;
        let maxQueryAndVariablesLength = 1300;
        let tQuery = 'query';
        let tMutation = 'mutation';

        const type = query.trim().toLowerCase().indexOf(tQuery) === 0 ? tQuery : tMutation;
        if (method === 'GET' && (query.length + JSON.stringify(variables).length) > maxQueryAndVariablesLength) {
            method = 'POST';
        }
        if (type === tQuery) {
            // find the query name from the query and use that in the querystring to help with debugging to distinguish this request
            queryName = query.substring(query.indexOf(tQuery) + tQuery.length, query.indexOf('{')).trim();
        } else {
            queryName = query.substring(query.indexOf(tMutation) + tMutation.length, query.indexOf('(')).trim();
        }
        // remove any excess whitespace from the query only for GET requests since this is passed on the URL
        if (method === 'GET') {
            query = this.graphqlTrim(query);
        }
        let response: any = {};

        try {
            var graphQLBaseUrl = environment.mapcoreApi + '/graphql';
            response = await this.request<{ data: any }>(`${graphQLBaseUrl}?${queryName}&pdauthAppName=${environment.pdauthAppName}`, {
                body: method !== 'GET' ? {
                    query,
                    variables
                } : undefined,
                method,
                params: method === 'GET' ? {
                    query,
                    variables: JSON.stringify(variables)
                } : undefined
            });
        } catch (e) {
            if (e && !e.message && e.errors && e.errors[0]) {
                e.message = e.errors[0].message;
            }
            throw e;
        }
        if (!dataPath) {
            return <T>response.data;
        } else {
            const parts = dataPath.split('.');
            let result = response;
            if (response.data) {
                result = response.data;
            }
            for (const part of parts) {
                result = result[part];
            }
            return <T>result;
        }
    }

    async request<T>(url: string, config: any): Promise<T> {
        if (config.body) {
            config.body = JSON.stringify(config.body);
        }
        config = {
            headers: {
                'Content-Type': 'application/json'
            },
            params: {},
            ...config
        };

        // remove any trailing slash from the url
        if (url.lastIndexOf('/') === url.length - 1) {
            url = url.substring(0, url.length - 1);
        }

        if (config.method === 'GET') {
            url = url + '&query=' + config.params.query + '&variables=' + config.params.variables;
            return this.http2Service.get<any>(url, { withCredentials: true }).then(result => {
                return result.data;
            });
        } else {
            return this.http2Service.post<any>(url, config.body, { withCredentials: true }).then(result => {
                return result.data;
            });
        }
    }

    getRestUrlFromUrlComponents(urlComponents: Array<string>) {
        urlComponents.forEach((component, index) => {
            urlComponents[index] = component.toString();
        });
        if (urlComponents[0].indexOf('//') < 0) {
            urlComponents.unshift(environment.appPath + '/api');
        }
        return urlComponents.join('/');
    }

    graphqlTrim(text: string) {
        text = text.replace(/\s+/g, ' ');
        return text;
    }
}